"use client";

import { useState } from "react";
import {
  X,
  Plus,
  AlertCircle,
  Users,
  Calendar,
  MapPin,
  FileText
} from "lucide-react";

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`;

const AddMeeting = ({ closeAddEvent, onEventAdded }) => {
  const [formData, setFormData] = useState({
    titre: "",
    objectif: "",
    date_heure: "",
    duree: 60,
    salle: "",
    participants_data: [{ name: "", email: "" }]
  });

  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getToken = () => localStorage.getItem("access_token");

  const getCurrentDateTime = () => {
    const now = new Date();
    const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return localDateTime.toISOString().slice(0, 16);
  };

  // Obtenir la date maximale (1 mois après aujourd'hui)
  const getMaxDateTime = () => {
    const now = new Date();
    const maxDate = new Date(now);
    maxDate.setMonth(maxDate.getMonth() + 1); // Ajouter 1 mois
    const localMaxDateTime = new Date(maxDate.getTime() - maxDate.getTimezoneOffset() * 60000);
    return localMaxDateTime.toISOString().slice(0, 16);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Validation spéciale pour la date
    if (name === "date_heure" && value) {
      const selectedDate = new Date(value);
      const maxDate = new Date();
      maxDate.setMonth(maxDate.getMonth() + 1);
      
      if (selectedDate > maxDate) {
        setFieldErrors(prev => ({ 
          ...prev, 
          date_heure: "La date de réunion ne peut pas dépasser 1 mois" 
        }));
      } else {
        setFieldErrors(prev => ({ ...prev, date_heure: null }));
      }
    }
    
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleParticipantChange = (index, field, value) => {
    const updated = [...formData.participants_data];
    updated[index][field] = value;
    setFormData((prev) => ({ ...prev, participants_data: updated }));
  };

  const addParticipant = () => {
    setFormData((prev) => ({
      ...prev,
      participants_data: [...prev.participants_data, { name: "", email: "" }],
    }));
  };

  const removeParticipant = (index) => {
    if (formData.participants_data.length > 1) {
      setFormData((prev) => ({
        ...prev,
        participants_data: prev.participants_data.filter((_, i) => i !== index),
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.titre.trim()) errors.titre = "Le titre est requis";
    if (!formData.date_heure) errors.date_heure = "La date est requise";
    if (!formData.salle.trim()) errors.salle = "La salle est requise";

    // Validation de la date (pas plus d'un mois)
    if (formData.date_heure) {
      const selectedDate = new Date(formData.date_heure);
      const now = new Date();
      const maxDate = new Date();
      maxDate.setMonth(maxDate.getMonth() + 1);
      
      if (selectedDate < now) {
        errors.date_heure = "La date ne peut pas être dans le passé";
      } else if (selectedDate > maxDate) {
        errors.date_heure = "La date ne peut pas dépasser 1 mois à partir d'aujourd'hui";
      }
    }

    const invalidParticipants = formData.participants_data.filter(
      p => !p.name.trim() || !p.email.trim()
    );
    if (invalidParticipants.length > 0) {
      errors.participants = "Tous les participants doivent avoir un nom et un email";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = formData.participants_data.filter(
      p => p.email && !emailRegex.test(p.email)
    );
    if (invalidEmails.length > 0) {
      errors.participants = "Certains emails sont invalides";
    }

    return errors;
  };

  const convertToUTC = (localDateTime) => {
    const date = new Date(localDateTime);
    const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return utcDate.toISOString().slice(0, 16);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError("Veuillez corriger les erreurs");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setFieldErrors({});

    try {
      const token = getToken();
      if (!token) {
        setError("Vous devez être connecté");
        setIsSubmitting(false);
        return;
      }

      const utcDateTime = convertToUTC(formData.date_heure);

      const meetingData = {
        titre: formData.titre,
        objectif: formData.objectif,
        date_heure: utcDateTime,
        duree: parseInt(formData.duree),
        salle: formData.salle,
        participants_data: formData.participants_data.filter(p => p.name && p.email)
      };

      const response = await fetch(`${API_URL}/reunions/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(meetingData)
      });

      const responseData = await response.json();

      if (!response.ok) {
        if (responseData.participants_data) {
          setError(responseData.participants_data[0]);
        } else if (responseData.detail) {
          setError(responseData.detail);
        } else if (typeof responseData === 'object') {
          const firstError = Object.values(responseData)[0];
          setError(Array.isArray(firstError) ? firstError[0] : String(firstError));
        } else {
          setError("Erreur lors de la création de la réunion");
        }
        setIsSubmitting(false);
        return;
      }

      if (onEventAdded) {
        onEventAdded(responseData);
      }
      closeAddEvent(false);

    } catch (err) {
      console.error("Erreur:", err);
      setError("Erreur de connexion au serveur");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-xl shadow-xl flex flex-col">

        <div className="bg-gradient-to-r from-[#56C596] to-[#5e17eb] text-white px-6 py-4 flex justify-between items-center rounded-t-xl">
          <h2 className="text-xl font-semibold">Ajouter une réunion</h2>
          <button
            onClick={() => closeAddEvent(false)}
            className="p-1 hover:bg-white/20 rounded-full transition"
          >
            <X />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-6">

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 text-red-700 flex gap-2 rounded">
              <AlertCircle size={18} className="flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titre de la réunion <span className="text-red-500">*</span>
            </label>
            <input
              name="titre"
              value={formData.titre}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#5e17eb] focus:border-transparent ${fieldErrors.titre ? "border-red-500" : "border-gray-300"
                }`}
              placeholder="Ex: Sprint planning"
            />
            {fieldErrors.titre && (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.titre}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date et heure <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="datetime-local"
                name="date_heure"
                min={getCurrentDateTime()}
                max={getMaxDateTime()}
                value={formData.date_heure}
                onChange={handleChange}
                className={`w-full pl-10 p-3 border rounded-lg focus:ring-2 focus:ring-[#5e17eb] focus:border-transparent ${fieldErrors.date_heure ? "border-red-500" : "border-gray-300"
                  }`}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              ⏰ La réunion doit être planifiée dans les 30 prochains jours maximum
            </p>
            <p className="text-xs text-gray-500">
              L'heure sera enregistrée en UTC et affichée dans votre fuseau horaire
            </p>
            {fieldErrors.date_heure && (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.date_heure}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Durée (minutes)
            </label>
            <input
              type="number"
              name="duree"
              value={formData.duree}
              onChange={handleChange}
              min="15"
              step="15"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5e17eb] focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Salle <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                name="salle"
                value={formData.salle}
                onChange={handleChange}
                className={`w-full pl-10 p-3 border rounded-lg focus:ring-2 focus:ring-[#5e17eb] focus:border-transparent ${fieldErrors.salle ? "border-red-500" : "border-gray-300"
                  }`}
                placeholder="Ex: Salle A / Meet Link"
              />
            </div>
            {fieldErrors.salle && (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.salle}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Objectif
            </label>
            <textarea
              name="objectif"
              value={formData.objectif}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5e17eb] focus:border-transparent"
              rows={3}
              placeholder="Décrivez l'objectif de la réunion..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Participants <span className="text-red-500">*</span>
            </label>

            {formData.participants_data.map((p, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  placeholder="Nom complet"
                  value={p.name}
                  onChange={(e) =>
                    handleParticipantChange(i, "name", e.target.value)
                  }
                  className="flex-1 p-2 border border-gray-300 rounded focus:ring-1 focus:ring-[#5e17eb]"
                />
                <input
                  placeholder="Email"
                  type="email"
                  value={p.email}
                  onChange={(e) =>
                    handleParticipantChange(i, "email", e.target.value)
                  }
                  className="flex-1 p-2 border border-gray-300 rounded focus:ring-1 focus:ring-[#5e17eb]"
                />
                {formData.participants_data.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeParticipant(i)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded transition"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            ))}

            {fieldErrors.participants && (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.participants}</p>
            )}

            <button
              type="button"
              onClick={addParticipant}
              className="text-[#5e17eb] text-sm mt-2 flex items-center gap-1 hover:gap-2 transition-all"
            >
              <Plus size={16} /> Ajouter un participant
            </button>
          </div>
        </div>

        <div className="border-t p-4 flex justify-end gap-3 bg-gray-50">
          <button
            onClick={() => closeAddEvent(false)}
            disabled={isSubmitting}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2 bg-[#5e17eb] text-white rounded-lg hover:bg-[#7c3aed] transition disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin">⏳</span>
                Création...
              </>
            ) : (
              "Créer la réunion"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMeeting;